import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, round } from 'lodash';
import FileUpload from '../File/FileUploader';
import FileUploadActions from '@v7/form/src/utils/FileUploadActions';
import { RootState } from '@v7/form/src/store';
import { useGetFileDetailsQuery, useUploadFileMutation } from '@v7/form/src/store/services/updateFormBuilder';
import { uploadFileToS3 } from '@v7/form/src/store/services/api';
import { updateFileUploadStatus, updateFormFileData } from '@v7/form/src/store/actions';
import { readFileData } from '@v7/form/src/utils';
import { getItem } from '@v7/form/src/utils/store';
import { UPLOAD_STATUS, VALIDATION_ERROR_MESSAGES } from '@v7/form/src/utils/constant';
import FieldLabel from '@v7/form/src/components/Tools/FieldLabel';
import {
  failFileUploadNotification,
  successFileUploadNotification,
} from '@v7/form/src/components/Notifications/ToastNotification';
import { FileDownloadFileData } from '@v7/form/src/containers/BlockNoteRenderer/types';
import { handleInputValidation, handleInvalidField } from '@v7/form/src/components/Tools/TriggerValidation/inputUtils';
import TriggerValidation from '@v7/form/src/components/Tools/TriggerValidation';
import VideoPreview from '@v7/form/src/components/Tools/Video/videoPreview';
import { ClearFileEvent, FileUploadEvent, FileUploadResponse, UploadStatusType, VideoProps } from './types';

const videoFileTypes = ['MP4'];

const Video = (props: VideoProps) => {
  const {
    required,
    label,
    name,
    disabled,
    updatable,
    pre_filled,
    fileTypes,
    size = 30,
    error,
    onChange,
    readOnly,
    tooltip,
    attrkey,
    value,
    previewWidth,
    id,
    ...rest
  } = props;
  const dispatch = useDispatch();
  const { fileUploadStatus } = useSelector((state: RootState) => state?.form) || {};
  const { user_id } = getItem('user_details') || {};
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileSelected, setFileSelected] = useState<any>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [validation, setValidation] = useState({
    triggerInvalid: false,
    message: '',
  });

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    handleInvalidField(event, setValidation);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputValidation(event, setValidation);
  };

  const uploadStatus = get(fileUploadStatus, attrkey ?? '');

  const [uploadFile] = useUploadFileMutation();
  const { data: file_download_file } = useGetFileDetailsQuery(
    {
      file_id: value,
    },
    {
      skip: typeof value !== 'number',
    },
  ) as { data: FileDownloadFileData | undefined };

  const setUploadStatus = (status: UploadStatusType) =>
    attrkey && dispatch(updateFileUploadStatus({ [attrkey]: status }));

  const onFileUploadSuccess = (fileId: number) => {
    const file = get(inputRef.current, 'files.0');
    setUploadStatus(UPLOAD_STATUS.SUCCESS);
    if (file) onChange?.({ file, fileId });
    successFileUploadNotification();
    setValidation((prev) => ({ ...prev, triggerInvalid: false }));
    if (id) {
      dispatch(updateFormFileData({ [id]: fileId }));
    }
  };

  const onFileUploadFailure = () => {
    setUploadStatus(UPLOAD_STATUS.FAILED);
    if (inputRef.current) inputRef.current.value = '';
    failFileUploadNotification();
  };

  const uploadCallback = (res: FileUploadResponse) => {
    const fileUploadData = get(res, 'data.file_upload_file', {}) as {
      id?: number;
      presigned_url?: string;
    };
    const { id, presigned_url } = fileUploadData;
    const file = get(inputRef.current, 'files.0');
    if (!presigned_url || !file) {
      onFileUploadFailure();
    } else {
      id && uploadFileToS3(presigned_url, file, id, onFileUploadSuccess, onFileUploadFailure);
    }
  };

  const onFileSelect = (e: FileUploadEvent) => {
    const file = get(e, 'target.files.0');

    if (!file) return;
    const fileName = get(file, 'name');
    const fileType = fileName?.split('.')?.pop();

    if (!file.type.startsWith('video/')) {
      setValidation({
        triggerInvalid: true,
        message: 'Please select a valid video file',
      });
      return;
    }
    const handleFileUpload = () => {
      setUploadStatus(UPLOAD_STATUS.UPLOADING);
      setFileSelected({
        fileName,
        fileType,
        file_type_id: 27,
        fileSize: round(get(file, 'size', 0) / (1024 * 1024), 2) || 0,
      });
      uploadFile({
        fileName,
        file_type_id: 27,
        fileType,
        user_id,
        uploadCallback,
      });
    };

    readFileData(file, (dataURL) => {
      if (inputRef.current) {
        (inputRef.current as any).dataURL = dataURL;
      }

      const videoObjectUrl = URL.createObjectURL(file);
      setVideoUrl(videoObjectUrl);

      handleFileUpload();
    });
  };

  const onClearFile = (e: ClearFileEvent) => {
    e.stopPropagation();
    setFileSelected(null);
    if (inputRef.current) inputRef.current.value = '';
    dispatch(updateFormFileData({ [attrkey]: null }));
    setVideoUrl(null);

    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    if (id) {
      dispatch(updateFormFileData({ [id]: null }));
    }
  };

  useEffect(() => {
    inputRef.current?.setCustomValidity(
      required && (!fileSelected || uploadStatus !== UPLOAD_STATUS.SUCCESS)
        ? VALIDATION_ERROR_MESSAGES.REQUIRED
        : fileSelected?.fileSize && size && fileSelected?.fileSize > parseFloat(size.toString())
          ? `Exceeds file size of ${size} MB`
          : '',
    );
    setValidation((prev) => ({
      ...prev,
      message: inputRef?.current?.validationMessage || '',
    }));
  }, [fileSelected, uploadStatus]);

  useEffect(() => {
    if (file_download_file) {
      const { id, original_filename, resource_url } = file_download_file || {};
      setFileSelected({
        id,
        fileName: original_filename,
        resource_url,
        fileType: original_filename?.split('.')?.pop(),
      });
      setUploadStatus(UPLOAD_STATUS.SUCCESS);
      setVideoUrl(resource_url);
    }
  }, [file_download_file]);

  const onFileClickFn = () => {
    if (!readOnly && !(uploadStatus === UPLOAD_STATUS.UPLOADING) && !(!updatable && pre_filled))
      inputRef?.current && inputRef.current.click();
  };

  const fileTypeList = videoFileTypes.join(', ');

  return (
    <div
      className={`gap-8 cursor-pointer bg-white hover:shadow-lg transition-shadow duration-200 w-fit pb-[27px] px-2`}
    >
      <div className="flex">
        {label && (
          <FieldLabel
            name={name}
            value={label}
            required={required}
            className={!updatable && pre_filled ? 'group group-hover:cursor-pointer' : ''}
            tooltip={tooltip}
          >
            {label}
          </FieldLabel>
        )}
        <input
          {...rest}
          ref={inputRef}
          type="file"
          accept="video/*"
          name={name}
          onChange={onFileSelect}
          id={id}
          className="opacity-0 pointer-events-none w-0.5"
          aria-describedby={`${name}-error`}
          data-testid={rest['data-testid'] || 'input'}
          role="button"
          onInvalid={handleInvalid}
          onInput={handleInput}
        />
      </div>

      <VideoPreview videoUrl={videoUrl} />

      {!videoUrl && (
        <>
          <FileUpload
            fileSelected={fileSelected}
            onFileClickFn={onFileClickFn}
            onClearFile={onClearFile}
            message={'Upload a video file'}
            getFileTrailingIcon={() => <FileUploadActions uploadStatus={uploadStatus} onClearFile={onClearFile} />}
            fileTypeList={fileTypeList}
            size={size}
            disabled={disabled}
            updatable={updatable}
            pre_filled={pre_filled}
            onFileUpload={onFileSelect}
          />
          {error && (
            <p
              className="font-normal text-xs text-rose-800 mt-2"
              id={`${name}-description`}
              data-testid="input_error_msg"
            >
              {error}
            </p>
          )}
          <TriggerValidation
            validationTrigger={validation?.triggerInvalid}
            validationMessage={validation?.message}
            name={name}
            alterClassName="mt-2"
          />
        </>
      )}
    </div>
  );
};

export default memo(Video);
